import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SectionNavSectionDef, SectionNavValue } from '@easyflow/ui-frontend/components/ui/section-nav';

export interface UseSectionNavStateOptions {
  sections: SectionNavSectionDef[];
  defaultSection?: string;
  defaultSub?: string;
  sectionParam?: string;
  subParam?: string;
  replace?: boolean;
}

export interface UseSectionNavStateResult {
  value: SectionNavValue;
  setValue: (next: SectionNavValue) => void;
  activeSection: SectionNavSectionDef | undefined;
  activeSub: string | undefined;
}

function pickValid(
  sections: SectionNavSectionDef[],
  rawSection: string | null,
  rawSub: string | null,
  defaultSection: string | undefined,
  defaultSub: string | undefined,
): SectionNavValue {
  const first = sections[0];
  const fallbackSection =
    (defaultSection && sections.find(s => s.value === defaultSection)?.value) ?? first?.value ?? '';
  const sectionValue =
    (rawSection && sections.find(s => s.value === rawSection)?.value) || fallbackSection;
  const active = sections.find(s => s.value === sectionValue);
  const subs = active?.subs ?? [];
  if (subs.length === 0) {
    return { section: sectionValue, sub: undefined };
  }
  const fallbackSub = (defaultSub && subs.find(s => s.value === defaultSub)?.value) ?? subs[0]?.value;
  const subValue = (rawSub && subs.find(s => s.value === rawSub)?.value) || fallbackSub;
  return { section: sectionValue, sub: subValue };
}

export function useSectionNavState(opts: UseSectionNavStateOptions): UseSectionNavStateResult {
  const {
    sections,
    defaultSection,
    defaultSub,
    sectionParam = 'section',
    subParam = 'sub',
    replace = true,
  } = opts;
  const [searchParams, setSearchParams] = useSearchParams();

  const value = useMemo(
    () => pickValid(sections, searchParams.get(sectionParam), searchParams.get(subParam), defaultSection, defaultSub),
    [sections, searchParams, sectionParam, subParam, defaultSection, defaultSub],
  );

  const setValue = useCallback(
    (next: SectionNavValue) => {
      const params = new URLSearchParams(searchParams);
      params.set(sectionParam, next.section);
      if (next.sub) params.set(subParam, next.sub);
      else params.delete(subParam);
      setSearchParams(params, { replace });
    },
    [searchParams, setSearchParams, sectionParam, subParam, replace],
  );

  const activeSection = useMemo(
    () => sections.find(s => s.value === value.section),
    [sections, value.section],
  );

  return { value, setValue, activeSection, activeSub: value.sub };
}
